import i18n from '@/utils/i18n';
import React, { ReactElement, useCallback, useMemo } from 'react';
import { createStyles } from '@/contexts/theme-context';
import { DetailsTabPanel } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/components/item-detail-content/details-tab/details-tab-panel';
import { ErrorState } from '@/experiences/sales-portal/components/error-state';
import { GeneralTabPanel } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/components/item-detail-content/general-tab/general-tab-panel';
import { ItemDetailContentData } from '@/experiences/sales-portal/domain/items/blocs/types/item-detail-content';
import { ItemDetailContentHeader } from '@/experiences/sales-portal/domain/items/components/item-detail-content-header';
import { ItemDetailDetailsLoadingSkeleton } from '@/experiences/sales-portal/domain/items/components/details-tab/details-tab-loading-skeleton';
import { ItemDetailEmptyState } from '@/experiences/sales-portal/domain/items/components/item-detail-empty-state';
import { ItemDetailGeneralLoadingSkeleton } from '@/experiences/sales-portal/domain/items/components/general-tab/general-tab-loading-skeleton';
import { ItemDetailPanelFooterProps } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/components/item-detail-content/item-detail-panel-footer';
import { ItemDetailTabBar } from '@/experiences/sales-portal/domain/items/components/item-detail-tab-bar';
import { ItemDetailTabKey } from '@/enums';
import { OrderEntryLabel } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/transformers/order-entry-table-transformers';
import { OrderEntryPriceEditDisplayHeader } from '@/types';
import { SidePanel } from '@usepepper/sprout-kit-ui';
import { useIsGrossProfitHidden } from '@/experiences/sales-portal/blocs/hooks/use-is-gross-profit-hidden';
import { useItemDetailContentLoader } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/loaders/use-item-detail-content-loader';
import { usePricingStore } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/stores/pricing-store';
import { useSaveSalesPortalCartMutator } from '@/experiences/sales-portal/domain/ordering/blocs/mutators/use-save-sales-portal-cart-mutator';
import { useVariantPackPrefetchLoader } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/loaders/use-variant-pack-prefetch-loader';
import { View } from 'react-native';

export enum ItemDetailRenderState {
  Empty = 'empty',
  Error = 'error',
  Loading = 'loading',
  Ready = 'ready',
}

/**
 * Props for {@link ItemDetailContent}.
 */
export interface ItemDetailContentProps {
  /** Currently selected tab. */
  activeTab: ItemDetailTabKey;
  chatUUID: string | null;
  /** Selected fulfillment date (YYYY-MM-DD); null when no fulfillment is selected. */
  deliveryDate: string | null;
  /** Docked add-to-cart footer; supplied by the parent only when the rep has drilled into a related variant pack. */
  FooterComponent?: React.ComponentType<ItemDetailPanelFooterProps>;
  /** When true, the quantity input in the footer is disabled (e.g. account on hold). */
  isQuantityDisabled?: boolean;
  /** Row labels for the General tab's Status card. */
  labels?: OrderEntryLabel;
  /**
   * When set, the header swaps the close X for a back arrow that calls this
   * handler. Used to pop the panel's variant-pack drill-down stack.
   */
  onBack?: () => void;
  /** Called when the close X is pressed. */
  onClose: () => void;
  /** Curried tab select handler — `onSelectTab(tab)` returns the onPress for that pill. */
  onSelectTab: (tab: ItemDetailTabKey) => () => void;
  /**
   * Called when a related variant pack row is pressed; the parent pushes
   * the pack onto the panel's drill-down stack.
   */
  onSelectVariantPack: (variantPackId: string) => void;
  /** Order guides the row belongs to (for the Status card). */
  orderGuideLabels: { level: string; orderGuideName: string }[];
  /**
   * Order-guide-level display-config headers — pass-through to the loader so
   * it can hydrate `displayConfigs` with `category` + localized `title`.
   */
  priceEditDisplayHeaders: OrderEntryPriceEditDisplayHeader[];
  /** Restaurant the panel was opened from. */
  restaurantId: string;
  restaurantTimezone: string;
  /** Supplier the panel was opened from. */
  supplierId: string;
  /** The variant pack to display. */
  variantPackId: string;
}

/**
 * Item detail panel shell. Owns the loader and render-state machine, renders
 * the header + tab pills, and delegates loading / error / empty / loaded
 * states to the appropriate subcomponent. The General tab body composes
 * status badges, read-only pricing info, and categorized display configs.
 * The Details tab body shows the product description, specs, shipping info,
 * and a list of related variant packs that can be drilled into via
 * `onSelectVariantPack`.
 *
 * @param props - {@link ItemDetailContentProps}.
 * @returns The rendered panel content.
 */
export const ItemDetailContent: React.FC<ItemDetailContentProps> = ({
  activeTab,
  chatUUID,
  deliveryDate,
  FooterComponent,
  isQuantityDisabled,
  labels,
  onBack,
  onClose,
  onSelectTab,
  onSelectVariantPack,
  orderGuideLabels,
  priceEditDisplayHeaders,
  restaurantId,
  restaurantTimezone,
  supplierId,
  variantPackId,
}) => {
  const [styles] = useStyles();
  const saveCart = useSaveSalesPortalCartMutator();
  const isGrossProfitHidden = useIsGrossProfitHidden(supplierId);
  const isSalesRepInfoDisabled =
    usePricingStore
      .getState()
      .editors.get(variantPackId)
      ?.isSalesRepInfoDisabled() ?? false;

  const { prefetchVariantPacks } = useVariantPackPrefetchLoader({
    chatUUID,
    deliveryDate,
    priceEditDisplayHeaders,
    restaurantId,
    supplierId,
  });

  const handleDetailLoaded = useCallback(
    (siblingVariantPackIds: string[]): void => {
      // Include the current pack id as a deep-link safety net: a refresh landing
      // directly on a sibling URL may not have its row cached either. The
      // prefetch hook filters out ids already present.
      void prefetchVariantPacks([variantPackId, ...siblingVariantPackIds]);
    },
    [prefetchVariantPacks, variantPackId],
  );

  const data = useItemDetailContentLoader({
    deliveryDate,
    onDetailLoaded: handleDetailLoaded,
    priceEditDisplayHeaders,
    restaurantId,
    restaurantTimezone,
    supplierId,
    variantPackId,
  });

  const { error, itemInfo, loading, refetch } = data;

  const renderState = useMemo<ItemDetailRenderState>(() => {
    if (error) {
      return ItemDetailRenderState.Error;
    }
    if (loading) {
      return ItemDetailRenderState.Loading;
    }
    if (isPanelDataEmpty(data)) {
      return ItemDetailRenderState.Empty;
    }
    return ItemDetailRenderState.Ready;
  }, [error, loading, data]);

  const stateBody = useMemo<ReactElement>(() => {
    if (renderState === ItemDetailRenderState.Loading) {
      return activeTab === ItemDetailTabKey.Details ? (
        <ItemDetailDetailsLoadingSkeleton />
      ) : (
        <ItemDetailGeneralLoadingSkeleton />
      );
    }
    if (renderState === ItemDetailRenderState.Empty) {
      return <ItemDetailEmptyState itemName={itemInfo.name} />;
    }
    if (activeTab === ItemDetailTabKey.General) {
      return (
        <GeneralTabPanel
          data={data}
          isGrossProfitHidden={isGrossProfitHidden}
          isSalesRepInfoDisabled={isSalesRepInfoDisabled}
          labels={labels}
          orderGuideLabels={orderGuideLabels}
        />
      );
    }
    return (
      <DetailsTabPanel
        currentVariantPackId={variantPackId}
        data={data}
        isQuantityDisabled={isQuantityDisabled}
        onSelectVariantPack={onSelectVariantPack}
        saveCart={saveCart}
      />
    );
  }, [
    activeTab,
    data,
    isGrossProfitHidden,
    isQuantityDisabled,
    itemInfo.name,
    labels,
    onSelectVariantPack,
    orderGuideLabels,
    renderState,
    isSalesRepInfoDisabled,
    saveCart,
    variantPackId,
  ]);

  return (
    <View style={styles.container}>
      <ItemDetailContentHeader
        externalItemId={itemInfo.externalItemId}
        imageUrl={itemInfo.imageUrl ?? null}
        itemName={itemInfo.name}
        onBack={onBack}
        onClose={onClose}
        packDisplayString={itemInfo.packDisplayString}
      />
      {renderState === ItemDetailRenderState.Error ? (
        <SidePanel.Body>
          <ErrorState
            description={i18n.t(
              'sales_portal.order_entry.item_detail.error.description',
            )}
            onPress={refetch}
            title={i18n.t('sales_portal.order_entry.item_detail.error.title')}
          />
        </SidePanel.Body>
      ) : (
        <View style={styles.tabsAndBody}>
          <ItemDetailTabBar activeTab={activeTab} onSelect={onSelectTab} />
          <View style={styles.bodyAndFooter}>
            <SidePanel.Body paddingTop={0}>{stateBody}</SidePanel.Body>
            {FooterComponent &&
            (renderState === ItemDetailRenderState.Ready ||
              renderState === ItemDetailRenderState.Loading) ? (
              <FooterComponent
                activeTab={activeTab}
                isQuantityDisabled={isQuantityDisabled}
                itemInfo={itemInfo}
                pricingSummary={data.pricingSummary}
                saveCart={saveCart}
                variantPackUUID={variantPackId}
              />
            ) : null}
          </View>
        </View>
      )}
    </View>
  );
};

const isPanelDataEmpty = ({
  displayConfigs,
  purchaseHistory,
  relatedVariantPacks,
  saltInfo,
}: ItemDetailContentData): boolean =>
  purchaseHistory.length === 0 &&
  relatedVariantPacks.length === 0 &&
  saltInfo === null &&
  displayConfigs.length === 0;

const useStyles = createStyles((theme) => ({
  bodyAndFooter: {
    flex: 1,
  },
  container: {
    flex: 1,
    flexDirection: 'column',
    gap: theme.sk.spacing.spaceMedium,
  },
  // Groups the tab bar with the body under a tighter gap than the
  // container's spaceMedium, keeping the header-to-tabs spacing intact.
  tabsAndBody: {
    flex: 1,
    gap: theme.sk.spacing.spaceXsmall,
  },
}));
